import React, { useEffect, useState } from "react";
import { Column, Sheet, Text } from "@amzn/meridian";
import PillButton from "src/components/UI/PillButton";
import { formatTimeRemaining } from "src/utils/dateUtil";
import "./DraftInspectionSheet.scss";

/** How often the expiry countdown re-renders while the sheet is open (ms). */
const TICK_INTERVAL_MS = 1000;

interface DraftInspectionSheetProps {
  open: boolean;
  /** Unix epoch SECONDS when the draft auto-clears; drives the live countdown. */
  expiresAt?: number;
  /** View & resume the existing draft. */
  onResume: () => void;
  /** Discard (orphan) the draft and start a brand new inspection. */
  onClearAndStartNew: () => void;
  /** Dismiss the sheet. */
  onClose: () => void;
}

/**
 * Bottom sheet shown from the Start Inspection flow when a resumable draft
 * exists for the scheduled assignment. It offers the user the choice to resume
 * the saved draft or discard it and start a fresh inspection.
 *
 * The expiry line counts down live from the draft's `expiresAt` (ticking every
 * second while the sheet is open). Once the draft has expired, the "resume"
 * action is disabled while "clear & start new" remains available.
 */
const DraftInspectionSheet: React.FC<DraftInspectionSheetProps> = ({
  open,
  expiresAt,
  onResume,
  onClearAndStartNew,
  onClose,
}) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!open) return;
    const intervalId = setInterval(() => setNow(Date.now()), TICK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [open]);

  const remaining = expiresAt != null ? formatTimeRemaining(expiresAt, now) : null;
  const isExpired = expiresAt != null && remaining === null;

  return (
    <Sheet type="overlay" side="bottom" open={open} onClose={onClose}>
      <Column spacing="400" spacingInset="500">
        <Text type="h300">You have an inspection in progress</Text>

        <Text type="b200" color="secondary">
          This inspection has saved progress. You can pick up where you left off
          or start a new inspection.
        </Text>

        {remaining !== null && (
          <Text type="b200" color="secondary">
            This draft will be automatically cleared in <strong>{remaining}</strong>{" "}
            unless the inspection is completed and submitted.
          </Text>
        )}
        {isExpired && (
          <Text type="b200" color="secondary">
            This draft has expired and will be cleared shortly.
          </Text>
        )}

        {/* Stacked full-width so the longer labels stay on a single line. */}
        <Column className="draft-inspection-sheet__actions" spacing="300">
          <PillButton
            text="VIEW & RESUME DRAFT"
            color="primary"
            onClick={onResume}
            disabled={isExpired}
          />
          <PillButton
            text="CLEAR DRAFT & START NEW"
            color="outline"
            onClick={onClearAndStartNew}
          />
        </Column>
      </Column>
    </Sheet>
  );
};

export default DraftInspectionSheet;
